"use client";

import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RevenueFaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    value: "item-1",
    question: "What is included in a revenue audit?",
    answer: "Our comprehensive revenue audit evaluates your current OTA performance, pricing parity, and distribution channel efficiency. We identify missed revenue opportunities and provide a data-backed roadmap to optimize your hotel's digital footprint and profitability."
  }, {
    value: "item-2",
    question: "How long does a typical engagement last?",
    answer: "Consulting engagements are tailored to your property's specific needs. While initial audits can be completed in 2–4 weeks, ongoing revenue management support is typically structured as a long-term partnership to ensure consistent growth and market adaptability."
  }, {
    value: "item-3",
    question: "Do I need to change my current tech stack?",
    answer: "Not necessarily. We specialize in optimizing your existing hospitality tech stack. If we identify gaps that hinder your revenue potential, we provide unbiased recommendations for tools that integrate seamlessly with your current operations."
  }, {
    value: "item-4",
    question: "What is your approach to OTA management?",
    answer: "We focus on high-conversion strategies, including optimized content, dynamic pricing, and strategic inventory management. Our goal is to increase your visibility on major platforms while maintaining brand integrity and maximizing direct booking potential."
  }, {
    value: "item-5",
    question: "Are your services suitable for boutique hotels?",
    answer: "Yes. Our revenue management strategies are highly scalable and effective for independent boutique hotels, luxury properties, and larger chains alike. We customize our approach to match your property's unique market position."
  }, {
    value: "item-6",
    question: "Can you help with staff training?",
    answer: "Absolutely. We believe in empowering your team. We offer training sessions on revenue management best practices, hospitality tech utilization, and data-driven decision-making to ensure your staff can sustain growth long-term."
  }, {
    value: "item-7",
    question: "How do you measure success?",
    answer: "We track key performance indicators such as RevPAR (Revenue Per Available Room), GOPPAR (Gross Operating Profit Per Available Room), and OTA conversion rates. Our reports provide clear, actionable insights into your property's financial health."
  }, {
    value: "item-8",
    question: "How soon can we expect results?",
    answer: "While some optimizations yield immediate improvements in visibility and booking flow, significant shifts in revenue performance typically materialize within 60 to 90 days as market data stabilizes and strategies take full effect."
  }];
  return <section data-section-id="3156" id="remotemanagementfaq" className="bg-background py-20 md:py-[120px] text-foreground">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              <p className="font-default text-xs tracking-[0.25em] uppercase text-secondary mb-6">
                Frequently Asked Questions
              </p>
              <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground mb-6">
                Expert <span className="italic font-light">Insights.</span>
              </h2>
              <p className="font-default text-base leading-relaxed text-muted-foreground mb-8 max-w-md">
                Answers to common questions about our revenue management and consulting services. Let's clarify how we can help your property thrive.
              </p>
              <Link to="/ContactPage" className="inline-block">
                <Button variant="ghost" className="group h-auto px-0 py-2 font-default text-sm tracking-wide text-foreground hover:bg-transparent hover:text-secondary">
                  <Mail className="size-4 stroke-[1.25] mr-2" />
                  <span>Contact our team</span>
                  <span className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">
                    →
                  </span>
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={faq.value} value={faq.value} className="border-b border-border last:border-b-0">
                  <AccordionTrigger className="group flex w-full items-center justify-between gap-6 py-8 text-left hover:no-underline [&>svg]:hidden">
                    <span className="font-default font-semibold text-xl md:text-[1.4rem] leading-snug text-foreground transition-colors duration-300 group-hover:text-secondary">
                      {faq.question}
                    </span>
                    <span className="relative flex size-8 shrink-0 items-center justify-center text-secondary">
                      <span className="absolute h-px w-4 bg-current" />
                      <span className="absolute h-4 w-px bg-current transition-transform duration-500 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:scale-0" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-12">
                    <p className="font-default text-base leading-[1.7] text-muted-foreground">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}